import { useQuery, useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useCallback } from 'react';
import { debounce } from 'lodash';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import createQueryConfig, { keys } from '../../queries';
import useMerchantSettings from '../../hooks/use-merchant-settings';
import { FetchMerchantOnboardingsV2Response } from '@shared/types';

export function useFetchOnboardingSteps() {
  const { queryClient, apiClient, eventBus } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);

  const { merchantSettings } = useMerchantSettings();
  const { merchantId, locale } = merchantSettings || {};

  const queryResult = useQuery<FetchMerchantOnboardingsV2Response>({
    ...queryConfig.merchantOnboardingsV2.fetchOnboardingSteps({
      merchantId,
      locale,
    }),
    enabled: Boolean(merchantId),
    retry: false,
  });

  // Create a debounced invalidation function
  const invalidateOnboardingQuery = useCallback(() => {
    if (!queryClient || !merchantId) return;

    const currentData = queryResult.data;

    if (!currentData) {
      const token =
        queryClient.getQueryData(['merchant_account_token']) || 'loading';

      const queryKey = keys.merchantOnboardingsV2.fetchOnboardingSteps(
        merchantId,
        String(token)
      );
      queryClient.invalidateQueries({ queryKey });
      return;
    }

    // Check if onboarding is completed
    const isCompleted =
      currentData?.overall_progress_percentage === 100 ||
      currentData?.completed_at !== null;

    if (!isCompleted) {
      const token =
        queryClient.getQueryData(['merchant_account_token']) || 'loading';

      const queryKey = keys.merchantOnboardingsV2.fetchOnboardingSteps(
        merchantId,
        String(token)
      );
      queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient, merchantId, queryResult.data]);

  // Debounce the invalidation with 1 second delay
  const debouncedInvalidateQuery = useMemo(
    () => debounce(invalidateOnboardingQuery, 1000),
    [invalidateOnboardingQuery]
  );

  // Set up event bus listeners to refetch onboarding data conditionally
  useEffect(() => {
    if (!eventBus || !queryClient || !merchantId) return;

    const eventsToListenFor = [
      'company_settings_updated',
      'service_created',
      'appointment_created',
    ];

    const handleConditionalRefetch = () => {
      // Use debounced invalidation
      debouncedInvalidateQuery();
    };

    // Register listeners
    eventsToListenFor.forEach((event) => {
      eventBus.on(event, handleConditionalRefetch);
    });

    // Cleanup listeners and cancel debounce on unmount
    return () => {
      eventsToListenFor.forEach((event) => {
        eventBus.off(event, handleConditionalRefetch);
      });

      // Cancel any pending debounced calls
      debouncedInvalidateQuery.cancel();
    };
  }, [eventBus, queryClient, merchantId, debouncedInvalidateQuery]);

  return queryResult;
}

export function useSkipOnboardingStep() {
  const { queryClient, apiClient } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);

  const { merchantSettings } = useMerchantSettings();
  const { merchantId } = merchantSettings || {};

  // Create debounced invalidation for mutations (shorter delay)
  const debouncedMutationInvalidate = useMemo(
    () =>
      debounce(() => {
        const token =
          queryClient.getQueryData(['merchant_account_token']) || 'loading';

        const queryKey = keys.merchantOnboardingsV2.fetchOnboardingSteps(
          merchantId,
          String(token)
        );

        queryClient.invalidateQueries({ queryKey });
      }, 1000),
    [queryClient, merchantId]
  );

  return useMutation({
    ...queryConfig.merchantOnboardingsV2.skipOnboardingStep({ merchantId }),
    onSuccess: () => {
      // Use debounced invalidation with 1 second delay
      debouncedMutationInvalidate();
    },
  });
}
